"use client";

import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Row } from "@tanstack/react-table";

import { Button } from "@/app/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu";
import { Flex } from "@radix-ui/themes";
import useCustomToast from "@/lib/hooks/useCustomToast";
import { AlertDialog } from "@/app/components/ui/alert-dialog";
import { useCallback, useRef, useState } from "react";
import { toTitleCase } from "@/lib/utils";
import { FlagIcon } from "lucide-react";
import { mutate } from "swr";
import UnflagBidderDialog from "../admin/auctions/[auctionId]/bids/UnflagBidderDialog";
import FlagBidderDialog from "../admin/auctions/[auctionId]/bids/FlagBidderDialog";

interface DataTableRowActionsProps<TData> {
    row: Row<TData>;
}

enum BidDialogType {
    FLAG,
    UNFLAG,
    DELETE
}

export type ClearableDialogHandle = {
    clear: () => void;
}

export function DataTableRowActions<TData>({
    row,
}: DataTableRowActionsProps<TData>) {
    const { original }: any = row;
    const { toast, toastError } = useCustomToast();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState<BidDialogType>();
    const [submitting, setSubmitting] = useState(false);
    const flagBidderDialogRef = useRef<ClearableDialogHandle>(null);

    const flagBidder = useCallback(async () => {
        setSubmitting(true);

        const response = await fetch("/api/v1/user/flag", {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId: original.id }),
        });

        mutate("/api/v1/bidders");

        setDialogOpen(false);
        setSubmitting(false);

        const verb = original.flagged ? "unflagged" : "flagged";

        if (!response.ok) {
            toastError({
                title: "Error",
                description: `An error has occurred. The user could not be ${verb}.`,
            });
        } else {
            toast({
                title: `${toTitleCase(verb)} user`,
                description: `${original.name} has been ${verb}.`,
            });
        }
    }, [original.id, original.name, original.flagged, setDialogOpen, setSubmitting, toast, toastError]);

    const isBidderFlagged = original?.flagged;

    const handleDialogOpenChange = (val: boolean) => {
        if (dialogType === BidDialogType.FLAG) {
            flagBidderDialogRef.current?.clear();
        }
        setDialogOpen(val);
    }

    return (
        <Flex justify="end">
            <AlertDialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                {(() => {
                    switch (dialogType) {
                        case BidDialogType.FLAG:
                            return <FlagBidderDialog ref={flagBidderDialogRef} onClick={flagBidder} email={original.email} submitting={submitting} />;
                        case BidDialogType.UNFLAG:
                            return <UnflagBidderDialog onClick={flagBidder} email={original.email} submitting={submitting} />;
                        default:
                            return null;
                    }
                })()}
            </AlertDialog>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
                        <DotsHorizontalIcon className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuItem className="text-sm" onClick={() => {
                        setDialogType(isBidderFlagged ? BidDialogType.UNFLAG : BidDialogType.FLAG);
                        setDialogOpen(true);
                    }}>
                        <Flex gap="2" align="center">
                            <FlagIcon className="h-3 w-3 mr-2" />
                            {isBidderFlagged ? "Unflag Bidder" : "Flag Bidder"}
                        </Flex>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </Flex>
    )
};